import { BasePageComponent } from "@/components/base/BasePageComponent";
import {
  PropertyAttributeCardComponent,
  PropertyAttributeData,
} from "@/components/property-attribute-card/property-attribute-card.component";
import { PropertyHelper } from "@/helpers/property.helper";
import { UserHelper } from "@/helpers/user.helper";
import { AppConfigService } from "@/services/app-config.service";
import { MenuToNative, MenuToNativeId } from "@/services/bridge/bridge.service";
import { DictionaryCacheService } from "@/services/dictionary-cache.service";
import { NavService } from "@/services/nav.service";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  PropertiesService,
  PropertyAttribute,
  PropertyCategory,
  RealProperty,
  SharingDataCategory,
} from "@eemo/api-client";
import { Subscription } from "rxjs";

import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { NgFor, NgIf } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { RoundedLinkComponent } from "../../../components/rounded-link/rounded-link.component";

@Component({
  selector: "app-property-infos",
  templateUrl: "./property-infos.component.html",
  styleUrls: ["./property-infos.component.scss"],
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    RoundedLinkComponent,
    TranslateModule,
    TooltipDirective,
    PropertyAttributeCardComponent,
  ],
})
export class PropertyInfosComponent
  extends BasePageComponent
  implements OnInit, OnDestroy
{
  list: MenuToNative[];

  prop: RealProperty;

  subs: Subscription[] = [];

  buildingData: PropertyAttributeData[] = [];
  energyData: PropertyAttributeData[] = [];
  financialData: PropertyAttributeData[] = [];
  zoneInfoData: PropertyAttributeData[] = [];
  canViewBuildingData = false;
  canViewEnergyData = false;
  canViewFinancialData = false;

  constructor(
    protected route: ActivatedRoute,
    public propHelper: PropertyHelper,
    public userHelper: UserHelper,
    protected navService: NavService,
    private appConfigService: AppConfigService,
    private propertyService: PropertiesService,
    private dictCacheService: DictionaryCacheService
  ) {
    super(route, navService);
  }

  ngOnInit() {
    this.list =
      this.navService
        .getNavEnvById(MenuToNativeId.PROP_INFO)
        ?.childs.filter((c) => !c.removed) || [];

    this.subs.push(
      this.appConfigService.selectedPropObs.subscribe((p) => {
        if (p) {
          this.prop = p;
          this.canViewBuildingData = this.userHelper.canViewCategory(
            this.prop,
            SharingDataCategory.BUILDING
          );
          this.canViewEnergyData = this.userHelper.canViewCategory(
            this.prop,
            SharingDataCategory.ENERGY
          );
          this.canViewFinancialData = this.userHelper.canViewCategory(
            this.prop,
            SharingDataCategory.FINANCIAL
          );
          this.loadData();
        }
      })
    );

    this.subs.push(
      this.propertyService
        .getRealPropertyZoningInfos(this.prop.uid)
        .subscribe((data) => {
          data.forEach((d) => {
            this.zoneInfoData.push({
              title: d.zoningLabel,
              value: d.valueLabel,
            });
          });
        })
    );
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }

  loadData() {
    if (this.canViewBuildingData) {
      this.subs.push(
        this.propertyService
          .getRealPropertyAttributes(this.prop.uid, PropertyCategory.BUILDING)
          .subscribe((data) => {
            const dataTmp: { [key: string]: PropertyAttribute } = {};
            data.forEach((d) => (dataTmp[d.code] = d));
            this.buildingData = [];
            this.dictCacheService
              .getAttributesForCategory(PropertyCategory.BUILDING)
              .forEach((attr) => {
                if (dataTmp[attr.code]) {
                  this.buildingData.push({
                    title: this.dictCacheService.getAttrDisplayNameFromCode(
                      attr.code
                    ),
                    value: this.dictCacheService.getAttrDisplayValueFromCode(
                      attr.code,
                      dataTmp[attr.code].value
                    ),
                  });
                }
              });
          })
      );
    }

    if (this.canViewEnergyData) {
      this.subs.push(
        this.propertyService
          .getRealPropertyAttributes(this.prop.uid, PropertyCategory.ENERGY)
          .subscribe((data) => {
            const dataTmp: { [key: string]: PropertyAttribute } = {};
            data.forEach((d) => (dataTmp[d.code] = d));
            this.energyData = [];
            this.dictCacheService
              .getAttributesForCategory(PropertyCategory.ENERGY)
              .forEach((attr) => {
                if (dataTmp[attr.code]) {
                  this.energyData.push({
                    title: this.dictCacheService.getAttrDisplayNameFromCode(
                      attr.code
                    ),
                    value: this.dictCacheService.getAttrDisplayValueFromCode(
                      attr.code,
                      dataTmp[attr.code].value
                    ),
                  });
                }
              });
          })
      );
    }

    if (this.canViewFinancialData) {
      this.subs.push(
        this.propertyService
          .getRealPropertyAttributes(this.prop.uid, PropertyCategory.FINANCIAL)
          .subscribe((data) => {
            const dataTmp: { [key: string]: PropertyAttribute } = {};
            data.forEach((d) => (dataTmp[d.code] = d));
            this.financialData = [];
            this.dictCacheService
              .getAttributesForCategory(PropertyCategory.FINANCIAL)
              .forEach((attr) => {
                if (dataTmp[attr.code]) {
                  this.financialData.push({
                    title: this.dictCacheService.getAttrDisplayNameFromCode(
                      attr.code
                    ),
                    value: this.dictCacheService.getAttrDisplayValueFromCode(
                      attr.code,
                      dataTmp[attr.code].value
                    ),
                  });
                }
              });
          })
      );
    }
  }
}
